import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Session } from "@shared/schema";

interface SessionsResponse {
  sessions: Session[];
}

const SESSION_DURATION_MIN: Record<string, number> = {
  practice: 60,
  qualifying: 60,
  sprint: 30,
  "sprint qualifying": 30,
  "sprint shootout": 30,
  race: 120,
};

function endTimeFor(session: Session): number {
  const start = new Date(session.date_start).getTime();
  const name = session.session_name?.toLowerCase() ?? "";
  let durationMin = 60;
  for (const [key, mins] of Object.entries(SESSION_DURATION_MIN)) {
    if (name.includes(key)) {
      durationMin = mins;
      break;
    }
  }
  return start + durationMin * 60 * 1000;
}

/**
 * Lightweight hook that returns whether an F1 session is happening right now.
 * Reuses the cached /api/sessions query so it shares data with the countdown widget.
 */
export function useIsSessionLive(): { isLive: boolean; liveSession: Session | null } {
  const year = new Date().getFullYear();
  const { data } = useQuery<SessionsResponse>({
    queryKey: ["/api/sessions", year],
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const sessions = data?.sessions ?? [];
  const now = Date.now();
  const live =
    sessions.find((s) => {
      const start = new Date(s.date_start).getTime();
      return start <= now && endTimeFor(s) >= now;
    }) ?? null;

  // Reference `tick` so the recomputation runs on the interval.
  void tick;

  return { isLive: !!live, liveSession: live };
}
